import { Button } from "../ui/button";
import { MessagesSquare, Pin, PinOff, Ban, Zap, PlayCircle, Square, Sparkles, Twitch } from "lucide-react";
import { YouTubeIcon } from "../YouTubeIcon";
import { t, tx, isEn } from "../../i18n";

function Badge({ m }) {
  if (m.isOwner) return <span className="text-[9px] bg-red-600 text-white rounded px-1">{t("DONO")}</span>;
  if (m.isMod) return <span className="text-[9px] bg-blue-500 text-white rounded px-1">MOD</span>;
  if (m.isMember) return <span className="text-[9px] bg-emerald-600 text-white rounded px-1">{t("MEMBRO")}</span>;
  return null;
}

function SourceIcon({ source }) {
  if (source === "twitch") return <Twitch className="h-5 w-5 text-white inline mr-1.5 align-middle" />;
  if (source === "youtube") return <YouTubeIcon className="mr-1.5 align-middle" />;
  return null;
}

const SOURCE_LABEL = {
  youtube: "Lendo chat do YouTube",
  twitch: "Lendo chat da Twitch",
  both: "Lendo chat do YouTube + Twitch",
};

export function ChatCuration({ enabled, demo, source, conn, messages, pinned, onStart, onStop, onPin, onUnpin, onReject, onAlert }) {
  const list = [...(messages || [])].reverse();
  const ytOk = !!conn?.youtube;
  const twOk = !!conn?.twitch;
  return (
    <div className="border-b border-border/60" data-testid="chat-curation">
      <div className="flex items-center justify-between px-3 py-2 bg-secondary/50 border-b border-border/60">
        <span className="text-[10px] font-mono tracking-[0.2em] text-foreground/80 flex items-center gap-1.5">
          <MessagesSquare className="h-3.5 w-3.5 text-primary" /> {t("CHAT AO VIVO")}
        </span>
        {enabled ? (
          <Button onClick={onStop} size="sm" variant="destructive" className="h-7 rounded-sm text-[11px]" data-testid="chat-stop-btn">
            <Square className="h-3 w-3 mr-1" /> {t("Parar")}
          </Button>
        ) : null}
      </div>

      {!enabled ? (
        <div className="p-3 space-y-2">
          {ytOk && twOk ? (
            <div className="grid grid-cols-3 gap-1.5">
              <Button onClick={() => onStart(false, "youtube")} size="sm" className="rounded-sm h-8 text-[11px] px-1" data-testid="chat-start-btn">
                <YouTubeIcon className="mr-1" /> YouTube
              </Button>
              <Button onClick={() => onStart(false, "twitch")} size="sm" className="rounded-sm h-8 text-[11px] px-1 bg-[#9146FF] hover:bg-[#7c3aed] text-white" data-testid="chat-start-twitch-btn">
                <Twitch className="!h-5 !w-5 mr-1" /> Twitch
              </Button>
              <Button onClick={() => onStart(false, "both")} size="sm" variant="secondary" className="rounded-sm h-8 text-[11px] px-1" data-testid="chat-start-both-btn">
                <PlayCircle className="h-3.5 w-3.5 mr-1" /> {t("Ambos")}
              </Button>
            </div>
          ) : (
            <Button onClick={() => onStart(false, twOk ? "twitch" : ytOk ? "youtube" : null)} size="sm" className="w-full rounded-sm h-8 text-xs" data-testid="chat-start-btn">
              {twOk ? <Twitch className="!h-5 !w-5 mr-1.5" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
              {twOk ? t("Iniciar chat da Twitch") : ytOk ? t("Iniciar chat do YouTube") : t("Iniciar chat (transmissão conectada)")}
            </Button>
          )}
          <Button onClick={() => onStart(true)} size="sm" variant="secondary" className="w-full rounded-sm h-8 text-xs" data-testid="chat-demo-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {t("Iniciar modo demonstração")}
          </Button>
          {(!ytOk || !twOk) && (
            <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid="chat-connect-hint">
              {!ytOk && (isEn()
                ? <>To read the <b>YouTube</b> chat, connect your stream first: the <YouTubeIcon className="mx-0.5 align-middle" /> button at the top of the screen (save the API key and paste the live link).{" "}</>
                : <>Para ler o chat do <b>YouTube</b>, conecte sua transmissão primeiro: botão <YouTubeIcon className="mx-0.5 align-middle" /> no topo da tela (salve a chave da API e cole o link da live).{" "}</>)}
              {!twOk && (isEn()
                ? <>For the <b>Twitch</b> chat, connect your channel with the <Twitch className="h-5 w-5 inline text-white mx-0.5 align-middle" /> button at the top.</>
                : <>Para o chat da <b>Twitch</b>, conecte seu canal no botão <Twitch className="h-5 w-5 inline text-white mx-0.5 align-middle" /> do topo.</>)}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="px-3 py-2 flex items-center gap-2 text-[11px] text-muted-foreground" data-testid="chat-source-status">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            {demo ? t("Modo demonstração ativo") : t(SOURCE_LABEL[source] || SOURCE_LABEL.youtube)}
          </div>
          {!ytOk && (demo || source === "twitch") && (
            <p className="px-3 pb-2 -mt-1 text-[10px] text-amber-400/90" data-testid="chat-yt-warning">
              {isEn()
                ? <>YouTube isn't connected — YouTube messages won't show up. Stop the chat, connect the live stream with the <YouTubeIcon className="mx-0.5 align-middle" /> button at the top and start again.</>
                : <>O YouTube não está conectado — mensagens do YouTube não vão aparecer. Pare o chat, conecte a live no botão <YouTubeIcon className="mx-0.5 align-middle" /> do topo e inicie novamente.</>}
            </p>
          )}

          {pinned && (
            <div className="mx-3 mb-2 rounded-sm border border-primary/40 bg-primary/10 p-2" data-testid="chat-pinned-banner">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-primary flex items-center gap-1"><Pin className="h-3 w-3" /> {t("Fixado na tela")}</span>
                <button onClick={onUnpin} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="chat-unpin-btn">
                  <PinOff className="h-3 w-3" /> {t("desafixar")}
                </button>
              </div>
              <p className="text-xs mt-1"><b>{pinned.author}:</b> {pinned.text}</p>
            </div>
          )}

          <div className="max-h-72 overflow-y-auto">
            {list.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">{t("Aguardando mensagens…")}</p>}
            {list.map((m) => (
              <div key={m.id} className={`px-3 py-2 border-b border-border/30 ${m.isSuper ? "bg-amber-500/10" : ""}`} data-testid={`chat-msg-${m.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 text-xs">
                    <SourceIcon source={m.source} />
                    <span className={`font-bold ${m.isSuper ? "text-amber-400" : "text-primary"}`}>{m.author}</span>{" "}
                    <Badge m={m} />
                    {m.isSuper && m.amount && <span className="text-amber-400 font-bold ml-1">{tx(m.amount)}</span>}
                    <div className="text-foreground/90 break-words">{m.text}</div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => onPin(m)} className="text-[10px] text-primary hover:text-primary/80 flex items-center gap-1" data-testid={`chat-pin-${m.id}`}>
                      <Pin className="h-3 w-3" /> {t("fixar")}
                    </button>
                    <button onClick={() => onReject(m.id)} className="text-[10px] text-destructive hover:text-destructive/80 flex items-center gap-1" data-testid={`chat-reject-${m.id}`}>
                      <Ban className="h-3 w-3" /> {t("reprovar")}
                    </button>
                    {m.isSuper && (
                      <button onClick={() => onAlert(m)} className="text-[10px] text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid={`chat-alert-${m.id}`}>
                        <Zap className="h-3 w-3" /> {t("alerta")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
